/**
 * Discount code: Component for copying a discount code to clipboard and managing price updates
 */
if (!customElements.get('discount-code')) {
  class DiscountCode extends HTMLElement {
    constructor() {
      super();
      this.discountCode = this.querySelector('.discount-code__code');
      this.discountCodeBtn = this.querySelector('.discount-code__btn');
      this.copySuccess = this.querySelector('.discount-code__success');
      
      // Price management elements
      this.discountBlock = this.closest('.product-discount-code');
      this.discountedPriceElement = this.discountBlock?.querySelector('[data-discounted-price]');
      this.originalPriceElement = this.discountBlock?.querySelector('[data-original-price]');
      
      this.init();
    }

    init() {
      // Initialize copy functionality
      if (this.discountCodeBtn && this.discountCode && navigator.clipboard && navigator.clipboard.writeText) {
        this.discountCodeBtn.addEventListener('click', this.copyCode.bind(this));
        this.discountCode.addEventListener('click', this.copyCode.bind(this));
      } else if (this.discountCodeBtn) {
        this.discountCodeBtn.hidden = true;
      }

      // Initialize variant-aware logic (block visibility + optional price updates)
      if (this.discountBlock) {
        this.initVariantManagement();
      }
    }

    initVariantManagement() {
      this.sectionId = this.discountBlock.dataset.sectionId;
      this.discountPercentage = parseFloat(this.discountBlock.dataset.discountPercentage) || null;
      this.discountAmount = parseFloat(this.discountBlock.dataset.discountAmount) || null;
      this.hiddenDiscountVariantIds = this.getHiddenDiscountVariantIds();

      // Find the product variants component scoped to this product section
      this.productRoot = document.querySelector(`#product-${this.sectionId}`) || this.discountBlock.closest(`[id="product-${this.sectionId}"]`);
      this.productVariants = this.productRoot?.querySelector(`product-variants[data-id="${this.sectionId}"]`) || null;

      if (this.productVariants) {
        // Listen for variant changes
        this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChange.bind(this));
      }

      this.onVariantChange();
    }

    getHiddenDiscountVariantIds() {
      const hiddenIdsRaw = this.discountBlock.dataset.hiddenDiscountVariantIds || '';
      const hiddenIds = hiddenIdsRaw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      return new Set(hiddenIds);
    }

    onVariantChange() {
      const currentVariant = this.productVariants?.currentVariant || null;
      this.updateBlockVisibility(currentVariant);
      this.updatePrices(currentVariant);
    }

    updateBlockVisibility(currentVariant) {
      if (!this.discountBlock) return;

      let shouldHideBlock = false;

      if (currentVariant && currentVariant.id != null) {
        shouldHideBlock = this.hiddenDiscountVariantIds.has(String(currentVariant.id));
      } else if (!this.productVariants) {
        shouldHideBlock = this.discountBlock.dataset.hideByCurrentVariant === 'true';
      } else {
        shouldHideBlock = false;
      }

      this.discountBlock.hidden = shouldHideBlock;
    }

    updatePrices(currentVariant) {
      if (!this.discountedPriceElement || !this.originalPriceElement || !currentVariant) {
        return;
      }

      const originalPrice = currentVariant.price;
      let discountedPrice = originalPrice;

      // Calculate discounted price
      if (this.discountPercentage && this.discountPercentage > 0) {
        const discountValue = originalPrice * (this.discountPercentage / 100);
        discountedPrice = originalPrice - discountValue;
      } else if (this.discountAmount && this.discountAmount > 0) {
        discountedPrice = originalPrice - this.discountAmount;
      }

      // Ensure price doesn't go below 0
      if (discountedPrice < 0) {
        discountedPrice = 0;
      }

      // Update the displayed prices using the same formatting as the theme
      this.discountedPriceElement.textContent = this.formatMoney(discountedPrice);
      this.originalPriceElement.textContent = this.formatMoney(originalPrice);
    }

    formatMoney(cents) {
      // Use the same money formatting function as the product variants component
      if (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format) {
        return this._formatMoney(cents, KROWN.settings.shop_money_format);
      }
      
      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return `${amount} Kč`;
    }

    _formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '{{ amount }}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount + (typeof KROWN !== 'undefined' && KROWN.settings ? KROWN.settings.iso_code || '' : ' Kč');
      }

      const match = formatString.match(placeholderRegex);
      if (!match) return formatWithDelimiters(cents, 2);

      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }

    copyCode() {
      const code = (this.discountCode?.dataset?.discountCode || '').trim();
      if (!code) return;
      navigator.clipboard.writeText(code).then(() => {
        if (!this.copySuccess) return;
        this.copySuccess.classList.remove('discount-code__success--hidden');

        this.copySuccess.animate(
          { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
          { duration: 200, easing: 'ease-out' }
        );

        setTimeout(() => {
          this.copySuccess
            .animate(
              { opacity: [1, 0], transform: ['translateY(0)', 'translateY(4px)'] },
              { duration: 200, easing: 'ease-in' }
            )
            .finished.then(() => {
              this.copySuccess.classList.add('discount-code__success--hidden');
            });
        }, 2200);
      });
    }
  }

  customElements.define('discount-code', DiscountCode);
}



